import {
    constants,
    currencyString,
    translatePaymentStatus,
} from "@qgiv/core-js";
import moment from "moment";

// U: Voided
// D: Declined
// E: Error
const negativeStatuses = ["U", "D", "E"];

/**
 * @param {object} currentTransHistory
 * @returns {boolean} True if error styling should be displayed, false otherwise.
 */
export const getDisplayErrorStyling = (currentTransHistory) => {
    const status = currentTransHistory?.status || "";

    const displayErrorStyling = !!negativeStatuses.includes(status);

    return displayErrorStyling;
};

/**
 * @param {object} currentTransHistory
 * @returns {boolean}
 */
export const getDisplayStatusIcon = (currentTransHistory) => {
    // When currentTransHistory.amount is less than 0 we know this is a refund.
    // We must consider the amount (refunds have a negative amount), as
    // refunded currentTransHistory maintain their status of "Accepted"
    const amount = currentTransHistory?.amount || "";
    const status = currentTransHistory?.status || "";

    const displayStatusIcon = !!(
        negativeStatuses.includes(status) || Number(amount) < 0
    );

    return displayStatusIcon;
};

/**
 * @param {object} currentTransHistory
 * @returns {boolean}
 */
export const getDisplayTransactionIdLink = (currentTransHistory) => {
    const receiptId = currentTransHistory?.receiptId || "";

    const displayTransactionIdLink = String(receiptId)?.length > 0;

    return displayTransactionIdLink;
};

/**
 * @param {number} index
 * @returns {boolean}
 */
export const getDisplayTopMargin = (index) => {
    const displayTopMargin = index > 0;
    return displayTopMargin;
};

/**
 * @param {object} props
 * @param {string} props.path
 * @param {string} props.receiptId
 * @param {string} props.template
 * @param {string} props.type
 * @returns {string}
 */
export const getTransactionLinkUrl = ({ path, receiptId, template, type }) => {
    const {
        ENUMS: {
            FormTemplate: { P2P_STANDARD, NEW_STANDARD_FORM },
            ProductType: { AUCTION },
        },
    } = constants;

    const isAuction = Number(type) === AUCTION;

    if (!path || !receiptId) return "";

    let transactionLinkUrl = "";

    if (Number(template) === P2P_STANDARD) {
        transactionLinkUrl = `/api/v1/donationform/confirmation/p2p/${receiptId}/`;
    }
    if (isAuction) {
        transactionLinkUrl = `/api/v1/donationform/confirmation/auction/${receiptId}/`;
    }
    if (Number(template) === NEW_STANDARD_FORM) {
        transactionLinkUrl = `/for/${path}/receipt/${receiptId}`;
    }

    return transactionLinkUrl;
};

/**
 * @param {object} params
 * @param {object} params.currentTransHistory
 * @param {string} params.transactionIdLabel
 * @returns {object}
 */
export const getFormattedTextTransactionId = ({
    currentTransHistory,
    transactionIdLabel,
}) => {
    // Get the text that is supposed to be rendered before and after the transaction id
    const formattedTextBeforeTransactionId =
        transactionIdLabel?.split("%TransactionID%")[0] || "";
    const formattedTextAfterTransactionId =
        transactionIdLabel?.split("%TransactionID%")[1] || "";

    const transactionId = currentTransHistory?.id || "";

    return {
        formattedTextAfterTransactionId,
        formattedTextBeforeTransactionId,
        transactionId,
    };
};

/**
 * @param {object} params
 * @param {string} params.currency
 * @param {object} params.currentTransHistory
 * @returns {string}
 */
export const getFormattedTransactionAmount = ({
    currency,
    currentTransHistory,
}) => {
    const amount = currentTransHistory?.amount || "0";

    const formattedTransactionAmount = currencyString(amount, currency);

    return formattedTransactionAmount;
};

/**
 * @param {object} currentTransHistory
 * @returns {string}
 */
export const getTranslatedStatus = (currentTransHistory) => {
    const amount = currentTransHistory?.amount || "";
    const status = currentTransHistory?.status || "";

    if (!status) return "";
    // Display Status. If the currentTransHistory is refunded we want to
    // display this as the status (FE representation only). Refunded
    // currentTransHistorys have a status of Accepted
    const translatedStatus =
        Number(amount) > 0 ? translatePaymentStatus(status) : "Refunded";

    return translatedStatus;
};

/**
 * @param {object} params
 * @param {object} params.currentTransHistory
 * @param {string} params.dateLabel
 * @returns {string}
 */
export const getFormattedTransactionDate = ({
    currentTransHistory,
    dateLabel,
}) => {
    const date = currentTransHistory?.date || "";

    const formattedTransactionDate = `${dateLabel}: ${date ? moment(date).format("MM/DD/YYYY") : ""}`;

    return formattedTransactionDate;
};

/**
 * @param {object} params
 * @param {string} params.currency
 * @param {object} params.currentTransHistory
 * @param {string} params.dateLabel
 * @param {number} params.index
 * @param {string} params.path
 * @param {string} params.transactionIdLabel
 * @returns {object}
 */
export const getHistoryListItemProps = ({
    currency,
    currentTransHistory,
    dateLabel,
    index,
    path,
    transactionIdLabel,
}) => {
    const displayErrorStyling = getDisplayErrorStyling(currentTransHistory);
    const displayStatusIcon = getDisplayStatusIcon(currentTransHistory);
    const displayTopMargin = getDisplayTopMargin(index);
    const displayTransactionIdLink =
        getDisplayTransactionIdLink(currentTransHistory);

    const {
        formattedTextAfterTransactionId,
        formattedTextBeforeTransactionId,
        transactionId,
        transactionLinkUrl,
    } = getFormattedTextTransactionId({
        currentTransHistory,
        path,
        transactionIdLabel,
    });

    const formattedTransactionAmount = getFormattedTransactionAmount({
        currency,
        currentTransHistory,
    });

    const formattedTransactionDate = getFormattedTransactionDate({
        currentTransHistory,
        dateLabel,
    });

    const translatedStatus = getTranslatedStatus(currentTransHistory);

    return {
        displayErrorStyling,
        displayStatusIcon,
        displayTopMargin,
        displayTransactionIdLink,
        formattedTextAfterTransactionId,
        formattedTextBeforeTransactionId,
        formattedTransactionAmount,
        formattedTransactionDate,
        transactionId,
        transactionLinkUrl,
        translatedStatus,
    };
};
